import React, { useState, useEffect } from "react";
import Moment from "react-moment";
import { FaRegEdit, FaSearch, FaChevronLeft, FaChevronRight, FaEye, FaTrash, FaGift } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Services } from "Services";
import { warningSwal } from "Util/Toast";
import SkeletonLoad from "Components/TableLoad/load";
import CommonClasses from "Styles/Common.module.css";
import { Helmet } from "react-helmet";
import Layout from "Components/Layout";
import { BiPlus } from "react-icons/bi";
import { TablePagination, Drawer, Box, Typography, Divider, Chip, Grid, Modal, Button } from "@mui/material";
import UserClasses from '../users/Users.module.css'

function Gifts() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [searchString, setSearchString] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedTrending, setSelectedTrending] = useState("");
    const [currentImageIndex, setCurrentImageIndex] = useState({});
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedGift, setSelectedGift] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [giftToDelete, setGiftToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [imageErrors, setImageErrors] = useState({});
    const token = { Authorization: `token ${localStorage.getItem("token")}` };

    const fetchGifts = (categoryId = null) => {
        setLoading(true);
        const params = categoryId ? `?CategoryID=${categoryId}` : '';
        Services.getGiftsWithParams("GET", null, token, params)
            .then((response) => {
                setLoading(false);
                if (response.Status === 1) {
                    setData(response.Gifts);
                } else if (response.Status === 0) {
                    warningSwal("Warning", response.Message);
                }
            })
            .catch((err) => {
                setLoading(false);
                alert("Something went wrong. Please try again.");
                console.error(err);
            });
    };

    const fetchCategories = () => {
        Services.getCategories("POST", null, token)
            .then((response) => {
                if (response.Status === 1) {
                    setCategories(response.Categories);
                }
            })
            .catch((err) => {
                console.error("Error fetching categories:", err);
            });
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchGifts(selectedCategory || null);
    }, [selectedCategory]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value);
        setPage(0); // Reset to first page when filter changes
    };

    const handleTrendingChange = (event) => {
        setSelectedTrending(event.target.value);
        setPage(0); // Reset to first page when filter changes
    };

    const filteredData = data.filter(item =>
        item.GiftName.toLowerCase().includes(searchString.toLowerCase()) &&
        (selectedCategory === "" || item.CategoryIDs?.includes(parseInt(selectedCategory))) &&
        (selectedTrending === "" || selectedTrending === "all" || item.IsTrending === parseInt(selectedTrending))
    );

    const slicedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const getCurrentImageIndex = (giftId) => {
        return currentImageIndex[giftId] || 0;
    };

    const nextImage = (giftId, totalImages) => {
        setCurrentImageIndex(prev => {
            const currentIndex = prev[giftId] || 0;
            const nextIndex = (currentIndex + 1) % totalImages;
            return {
                ...prev,
                [giftId]: nextIndex
            };
        });
    };

    const prevImage = (giftId, totalImages) => {
        setCurrentImageIndex(prev => {
            const currentIndex = prev[giftId] || 0;
            const prevIndex = (currentIndex - 1 + totalImages) % totalImages;
            return {
                ...prev,
                [giftId]: prevIndex
            };
        });
    };

    const toggleDrawer = (open, gift = null) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
        if (gift) {
            setSelectedGift(gift);
        }
    };

    const handleDeleteClick = (gift) => {
        setGiftToDelete(gift);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!giftToDelete) return;
        
        setDeleteLoading(true);
        try {
            const response = await Services.deleteGift(giftToDelete.GiftID, "DELETE", null, token);
            if (response.Status === 1) {
                // Remove the deleted gift from the data array
                setData(prevData => prevData.filter(gift => gift.GiftID !== giftToDelete.GiftID));
                setDeleteModalOpen(false);
                setGiftToDelete(null);
                warningSwal("Success", "Gift deleted successfully!");
            } else {
                warningSwal("Error", response.Message || "Failed to delete gift");
            }
        } catch (error) {
            console.error("Delete error:", error);
            warningSwal("Error", "Something went wrong. Please try again.");
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setGiftToDelete(null);
    };

    const handleImageError = (giftId) => {
        setImageErrors(prev => ({
            ...prev,
            [giftId]: true
        }));
    };

    return (
        <Layout Active={"Gifts"}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Gifts</title>
            </Helmet>
            <div className={CommonClasses["Container"]}>
                <div>
                    <h1 style={{ color: '#FF0D0D' }}>GIFTS</h1>
                </div>
                <div className={CommonClasses["control-btns"]}>
                    <div className={UserClasses["search-container"]} style={{ width: '200px' }}>
                        <FaSearch className={UserClasses["search-icon"]} />
                        <input
                            type="text"
                            placeholder="Search Gifts By Name"
                            style={{ textIndent: '15px' }}
                            value={searchString}
                            onChange={(e) => setSearchString(e.target.value)}
                            className={UserClasses["search-btn"]}
                        />
                    </div>
                    <div style={{ marginLeft: '20px' }}>
                        <select
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '14px',
                                minWidth: '200px'
                            }}
                        >
                            <option value="">All Categories</option>
                            {categories.map((category) => (
                                <option key={category.CategoryID} value={category.CategoryID}>
                                    {category.CategoryName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ marginLeft: '20px' }}>
                        <select
                            value={selectedTrending}
                            onChange={handleTrendingChange}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '14px',
                                minWidth: '200px'
                            }}
                        ><option value="all">All Gifts</option>
                            <option value="1">Trending</option>
                            <option value="0">Not Trending</option>
                        </select>
                    </div>
                    <button
                        className={CommonClasses["button"]}
                        onClick={() => {
                            navigate("/addgifts");
                        }}
                    >
                        <BiPlus size={20} /> Create New Gift
                    </button>
                </div>
                <div className={CommonClasses["wrapper"]}>
                    {loading ? (
                        <SkeletonLoad
                            TableHead={["GiftImage", "GiftName", "Material", "OfferPrice", "GiftRating", "RatingCount", "CreatedAt", "UpdateAt", "GiftStatus", "Actions"]}
                        />
                    ) : slicedData.length > 0 ? (
                        <div className={CommonClasses["Table"]}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>GiftId</th>
                                        <th>GiftImages</th>
                                        <th>GiftName</th>
                                        <th>OfferPrice</th>
                                        {/* <th>Gift Rating</th> */}
                                        {/* <th>Rating Count</th> */}
                                        <th>Is Returnable</th>
                                        <th>Is COD Available</th>
                                        <th>Is Trending</th>
                                        <th>CreatedAt</th>
                                        <th>GiftStatus</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {slicedData.map((item) => (
                                        <tr
                                            key={item.GiftID}
                                            id={item.GiftID}
                                            name={item.GiftName}
                                        >
                                            <td>{item.GiftID}</td>
                                            <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                {(!item.GiftImages || item.GiftImages.length === 0 || imageErrors[item.GiftID]) ? (
                                                    <div
                                                        style={{
                                                            width: "10rem",
                                                            height: "5rem",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            backgroundColor: "#f0f0f0",
                                                            borderRadius: "5px",
                                                            border: "2px solid #ddd",
                                                            margin: "0 auto"
                                                        }}
                                                    >
                                                        <FaGift size={40} color="#999" />
                                                    </div>
                                                ) : (
                                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                                        <div style={{ position: 'relative', width: "10rem", height: "5rem" }}>
                                                            <img
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "cover",
                                                                    borderRadius: '5px',
                                                                    opacity: item.QuantityAvailable === 0 ? 0.5 : 1,
                                                                    cursor: 'pointer'
                                                                }}
                                                                src={item.GiftImages[getCurrentImageIndex(item.GiftID)]}
                                                                alt={`gift-${item.GiftID}-${getCurrentImageIndex(item.GiftID)}`}
                                                                onError={() => handleImageError(item.GiftID)}
                                                                onClick={() => {
                                                                    window.open(item.GiftImages[getCurrentImageIndex(item.GiftID)], "_blank");
                                                                }}
                                                            />
                                                            
                                                            {/* Carousel Navigation - Only show if multiple images */}
                                                            {item.GiftImages.length > 1 && (
                                                                <>
                                                                    <button
                                                                        style={{
                                                                            position: 'absolute',
                                                                            left: '5px',
                                                                            top: '50%',
                                                                            transform: 'translateY(-50%)',
                                                                            background: 'rgba(0, 0, 0, 0.7)',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '50%',
                                                                            width: '28px',
                                                                            height: '28px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            cursor: 'pointer',
                                                                            fontSize: '14px',
                                                                            zIndex: 10,
                                                                            transition: 'background-color 0.2s ease'
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)'}
                                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            prevImage(item.GiftID, item.GiftImages.length);
                                                                        }}
                                                                    >
                                                                        <FaChevronLeft />
                                                                    </button>
                                                                    <button
                                                                        style={{
                                                                            position: 'absolute',
                                                                            right: '5px',
                                                                            top: '50%',
                                                                            transform: 'translateY(-50%)',
                                                                            background: 'rgba(0, 0, 0, 0.7)',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '50%',
                                                                            width: '28px',
                                                                            height: '28px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            cursor: 'pointer',
                                                                            fontSize: '14px',
                                                                            zIndex: 10,
                                                                            transition: 'background-color 0.2s ease'
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)'}
                                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            nextImage(item.GiftID, item.GiftImages.length);
                                                                        }}
                                                                    >
                                                                        <FaChevronRight />
                                                                    </button>
                                                                    {/* Image counter */}
                                                                    <div
                                                                        style={{
                                                                            position: 'absolute',
                                                                            bottom: '5px',
                                                                            right: '5px',
                                                                            background: 'rgba(0, 0, 0, 0.8)',
                                                                            color: 'white',
                                                                            padding: '3px 8px',
                                                                            borderRadius: '12px',
                                                                            fontSize: '11px',
                                                                            fontWeight: 'bold',
                                                                            zIndex: 10
                                                                        }}
                                                                    >
                                                                        {getCurrentImageIndex(item.GiftID) + 1}/{item.GiftImages.length}
                                                                    </div>
                                                                </>
                                                            )}
                                                            
                                                            {item.QuantityAvailable === 0 && (
                                                                <div
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: '0',
                                                                        left: '0',
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        display: 'flex',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center',
                                                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                                        color: 'white',
                                                                        fontSize: '12px',
                                                                        fontWeight: 'bold',
                                                                        borderRadius: '5px',
                                                                        zIndex: 5
                                                                    }}
                                                                >
                                                                    Out of Stock
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </td>

                                            <td>{item.GiftName}</td>
                                            <td>{item.OfferPrice}</td>
                                            {/* <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <span style={{ fontWeight: 'bold', color: '#FB3E7A' }}>
                                                        {item.GiftRating || 0}
                                                    </span>
                                                    <span style={{ color: '#666' }}>/ 5</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ color: '#666' }}>
                                                    {item.RatingCount || 0} members
                                                </span>
                                            </td> */}
                                            <td>
                                                {item.IsReturnable === 1 ? (
                                                    <div className={CommonClasses["Status-active"]}>
                                                        <span>Yes</span>
                                                    </div>
                                                ) : (
                                                    <div className={CommonClasses["Status-inactive"]}>
                                                        <span>No</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {item.IsCODAvailable === 1 ? (
                                                    <div className={CommonClasses["Status-active"]}>
                                                        <span>Yes</span>
                                                    </div>
                                                ) : (
                                                    <div className={CommonClasses["Status-inactive"]}>
                                                        <span>No</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {item.IsTrending === 1 ? (
                                                    <div className={CommonClasses["Status-active"]}>
                                                        <span>Yes</span>
                                                    </div>
                                                ) : (
                                                    <div className={CommonClasses["Status-inactive"]}>
                                                        <span>No</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <Moment format="DD-MM-YYYY">
                                                    {item.CreatedAt}
                                                </Moment>
                                            </td>
                                            <td>
                                                {item.GiftStatus === 1 ? (
                                                    <div className={CommonClasses["Status-active"]}>
                                                        <span>Active</span>
                                                    </div>
                                                ) : (
                                                    <div className={CommonClasses["Status-inactive"]}>
                                                        <span>Inactive</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className={CommonClasses["Action-btns"]}>
                                                    <button
                                                        onClick={toggleDrawer(true, item)}
                                                        style={{ marginRight: '8px' }}
                                                        title="View Details"
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    <button
                                                        onClick={() => navigate(`/updategift/${item.GiftID}`, { state: { gift: item } })}
                                                        style={{ marginRight: '8px' }}
                                                        title="Edit Gift"
                                                    >
                                                        <FaRegEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(item)}
                                                        style={{ color: '#dc3545' }}
                                                        title="Delete Gift"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <TablePagination
                                component="div"
                                count={filteredData.length}
                                page={page}
                                onPageChange={handlePageChange}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                rowsPerPageOptions={[5, 10, 25]}
                                className={CommonClasses["pagination"]}
                            />
                        </div>
                    ) : (
                        <div>
                            <p>No Gifts Found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Gift Details Drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 600,
                        padding: '20px',
                    },
                }}
            >
                <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
                    {selectedGift && (
                        <>
                            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#FF0D0D' }}>
                                Gift Details
                            </Typography>
                            
                            {/* Gift Images */}
                            <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>Images</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {selectedGift.GiftImages?.map((image, index) => (
                                    <img
                                        key={index}
                                        src={image}
                                        alt={`Gift ${index + 1}`}
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            objectFit: 'cover',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            border: '2px solid #e0e0e0'
                                        }}
                                        onClick={() => window.open(image, '_blank')}
                                    />
                                ))}
                            </Box>
                            <Divider sx={{ my: 2 }} />

                            {/* Basic Information */}
                            <Typography variant="h6" sx={{ mb: 1 }}>Basic Information</Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Gift ID</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedGift.GiftID}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Product ID</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedGift.ProductID}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Vendor ID</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedGift.VendorID}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Category ID</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedGift.CategoryID}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">Gift Name</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedGift.GiftName}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">Description</Typography>
                                    <Typography variant="body1">{selectedGift.GiftDescription}</Typography>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />

                            {/* Pricing Information */}
                            <Typography variant="h6" sx={{ mb: 1 }}>Pricing</Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Price</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>₹{selectedGift.Price}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Offer Price</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#FF0D0D' }}>₹{selectedGift.OfferPrice}</Typography>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />

                            {/* Product Details */}
                            <Typography variant="h6" sx={{ mb: 1 }}>Product Details</Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Material</Typography>
                                    <Typography variant="body1">{selectedGift.Material}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Capacity</Typography>
                                    <Typography variant="body1">{selectedGift.Capacity}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Product Dimension</Typography>
                                    <Typography variant="body1">{selectedGift.ProductDimension}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Top Diameter</Typography>
                                    <Typography variant="body1">{selectedGift.TopDiameter}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">Package Include</Typography>
                                    <Typography variant="body1">{selectedGift.PackageInclude}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Quantity Available</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: selectedGift.QuantityAvailable > 0 ? 'green' : 'red' }}>
                                        {selectedGift.QuantityAvailable}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />

                            {/* Status Information */}
                            <Typography variant="h6" sx={{ mb: 1 }}>Status & Features</Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Gift Status</Typography>
                                    <Chip 
                                        label={selectedGift.GiftStatus === 1 ? 'Active' : 'Inactive'} 
                                        color={selectedGift.GiftStatus === 1 ? 'success' : 'error'}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Is Returnable</Typography>
                                    <Chip 
                                        label={selectedGift.IsReturnable === 1 ? 'Yes' : 'No'} 
                                        color={selectedGift.IsReturnable === 1 ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Is COD Available</Typography>
                                    <Chip 
                                        label={selectedGift.IsCODAvailable === 1 ? 'Yes' : 'No'} 
                                        color={selectedGift.IsCODAvailable === 1 ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Is Trending</Typography>
                                    <Chip 
                                        label={selectedGift.IsTrending === 1 ? 'Yes' : 'No'} 
                                        color={selectedGift.IsTrending === 1 ? 'warning' : 'default'}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Is Bulk Uploaded</Typography>
                                    <Chip 
                                        label={selectedGift.IsBulkUploaded === 1 ? 'Yes' : 'No'} 
                                        color={selectedGift.IsBulkUploaded === 1 ? 'info' : 'default'}
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />

                            {/* Rating Information */}
                            <Typography variant="h6" sx={{ mb: 1 }}>Rating</Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Gift Rating</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#FB3E7A' }}>
                                        {selectedGift.GiftRating || 0}/5
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Rating Count</Typography>
                                    <Typography variant="body1">{selectedGift.RatingCount || 0} members</Typography>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />

                            {/* Timestamps */}
                            <Typography variant="h6" sx={{ mb: 1 }}>Timestamps</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">Created At</Typography>
                                    <Typography variant="body1">
                                        <Moment format="DD-MM-YYYY HH:mm:ss">{selectedGift.CreatedAt}</Moment>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">Updated At</Typography>
                                    <Typography variant="body1">
                                        <Moment format="DD-MM-YYYY HH:mm:ss">{selectedGift.UpdateAt}</Moment>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Box>
            </Drawer>

            {/* Delete Confirmation Modal */}
            <Modal
                open={deleteModalOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-modal-title"
                aria-describedby="delete-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2
                }}>
                    <Typography id="delete-modal-title" variant="h6" component="h2" sx={{ mb: 2, color: '#dc3545' }}>
                        Confirm Delete
                    </Typography>
                    <Typography id="delete-modal-description" sx={{ mb: 3 }}>
                        Are you sure you want to delete the gift "{giftToDelete?.GiftName}"? This action cannot be undone.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button 
                            onClick={handleDeleteCancel} 
                            variant="outlined" 
                            disabled={deleteLoading}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleDeleteConfirm} 
                            variant="contained" 
                            color="error"
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? 'Deleting...' : 'Delete'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Layout>
    );
}

export default Gifts;
